// src/context/ChatContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createDialog, 
  updateDialog, 
  listDialogs,
  getDialogDetails,
  removeDialog
} from '../services/dialogServices';
import {
  createConversation,
  listConversations,
  chatCompletion,
  getConversation,
} from '../services/conversationServices';
import {
  loadConversationFromCache,
  saveConversationToCache,
  saveDialogDetails,
  saveActiveSession,
  clearActiveSession
} from '../utils/localForageUtils';
import localforage from 'localforage';
import { getJsonLocalForage, setJsonLocalForage } from '../utils/localForageUtils';

// Import MCP services for agent mode support
import { AGENT_TYPES, executeBlenderMCP } from '../services/mcpServices';

// Import mobile optimizations
import { safeIndexedDBWrite, onVisibilityChange } from '../utils/mobileOptimizations';

// Create the context
const ChatContext = createContext(null);

/**
 * Provider component for chat/conversation state and operations
 */
export function ChatProvider({ children }) {
  // Active dialog & conversation
  const [dialogId, setDialogId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  // Add state for dialog KB IDs
  const [dialogKbIds, setDialogKbIds] = useState([]);
  
  // Add state to store all dialogs
  const [dialogs, setDialogs] = useState([]);
  
  // Chat messages
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Agent mode state
  const [agentMode, setAgentMode] = useState(AGENT_TYPES.ASK);
  const [currentBlenderServer, setCurrentBlenderServer] = useState('default');
  
  // Track page visibility to pause caching when hidden
  const [isPageVisible, setIsPageVisible] = useState(true);
  
  // Fetch dialog KB IDs whenever dialogId changes
  useEffect(() => {
    if (!dialogId) {
      setDialogKbIds([]);
      return;
    }
    
    let isMounted = true;
    
    async function fetchDialogKbIds() {
      try {
        const details = await getDialogDetails(dialogId, true);
        if (isMounted && details) {
          setDialogKbIds(details.kb_ids || []);
        }
      } catch (err) {
        console.error("Failed to fetch dialog details", err);
      }
    }
    
    fetchDialogKbIds();
    
    return () => { isMounted = false; };
  }, [dialogId]);
  
  // Load initial state from localForage, with validation
  useEffect(() => {
    async function loadInitialChatState() {
      // Load cached IDs immediately without waiting
      const cachedDialogId = await getJsonLocalForage('activeDialogId');
      const cachedConvId = await getJsonLocalForage('activeConversationId');
      
      if (cachedDialogId && cachedConvId) {
        console.debug('[ChatContext] Found cached chat state. Setting optimistically...');
        // Set immediately for instant UI
             setDialogId(cachedDialogId);
             setConversationId(cachedConvId);
             
        // Validate in background after initialization
        validateInBackground(cachedDialogId, cachedConvId);
      }
      
      // Fetch dialog list in background
      waitForInitThenFetchDialogs();
    }
    
    async function validateInBackground(dialogId, convId) {
      // Wait for initialization
      if (!window.__APP_INITIALIZED__) {
        await new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 50;
          
          const check = () => {
            if (window.__APP_INITIALIZED__ || attempts >= maxAttempts) {
              resolve();
            } else {
              attempts++;
              requestAnimationFrame(() => setTimeout(check, 100));
            }
          };
          
          check();
        });
      }
      
      // Validate cached state with API
      try {
        const dialogExists = await getDialogDetails(dialogId, true);
        const conversationExists = await getConversation(convId, true);
        
        if (dialogExists && conversationExists) {
          // Valid - update cache
          await saveDialogDetails(dialogId, {
            id: dialogId,
               kb_ids: dialogExists.kb_ids || [],
               project_id: dialogExists.project_id || null,
               name: dialogExists.name || 'Chat'
             });
          } else {
          // Invalid - clear
          console.warn('[ChatContext] Cached state validation failed. Clearing...');
             await localforage.removeItem('activeDialogId');
             await localforage.removeItem('activeConversationId');
          await localforage.removeItem(`conv-${convId}`);
             setDialogId(null);
             setConversationId(null);
          }
      } catch (error) {
        console.error('[ChatContext] Validation error:', error);
           await localforage.removeItem('activeDialogId');
           await localforage.removeItem('activeConversationId');
        await localforage.removeItem(`conv-${convId}`);
           setDialogId(null);
           setConversationId(null);
        }
    }
    
    async function waitForInitThenFetchDialogs() {
      // Wait for initialization
      if (!window.__APP_INITIALIZED__) {
        await new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 50;
          
          const check = () => {
            if (window.__APP_INITIALIZED__ || attempts >= maxAttempts) {
              resolve();
      } else {
              attempts++;
              requestAnimationFrame(() => setTimeout(check, 100));
            }
          };
          
          check();
        });
      }
      
      // Fetch dialog list
      refreshDialogList(false);
      }
    
    loadInitialChatState();
  }, []);
  
  // Listen for visibility changes to pause caching when tab is hidden
  useEffect(() => {
    const cleanup = onVisibilityChange((isHidden) => {
      setIsPageVisible(!isHidden);
    });
    return cleanup;
  }, []);
  
  // Persist active IDs whenever they change - with debouncing and visibility check
  useEffect(() => {
    if (!dialogId || !isPageVisible) return;
    
    const timeoutId = setTimeout(() => {
      safeIndexedDBWrite(() => setJsonLocalForage('activeDialogId', dialogId))
        .catch(err => {
          console.error('[ChatContext] Failed to cache dialogId:', err);
        });
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [dialogId, isPageVisible]);
  
  useEffect(() => {
    if (!conversationId || !isPageVisible) return;
    
    const timeoutId = setTimeout(() => {
      safeIndexedDBWrite(() => setJsonLocalForage('activeConversationId', conversationId))
        .catch(err => {
          console.error('[ChatContext] Failed to cache conversationId:', err);
        });
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [conversationId, isPageVisible]);
  
  // Load conversation whenever conversationId changes
  useEffect(() => {
    if (!conversationId) return;
    
    (async () => {
      const cached = await loadConversationFromCache(conversationId);
      if (cached && Array.isArray(cached)) {
        // Load cached messages with their references
        const cachedMessages = cached.map((m, idx) => ({ 
          ...m, 
          id: `msg-${idx}`,
          reference: m.reference || null 
        }));
        setMessages(cachedMessages);
        console.debug('[ChatContext] Loaded cached messages with references:', 
          cachedMessages.filter(m => m.reference).length, 'messages have references');
        
        // Temporarily disable background fetch to test citation functionality
        /*
        // also do a background fetch
        try {
          const conv = await getConversation(conversationId);
          console.debug('[ChatContext] Background fetch returned:', 
            conv?.message?.length || 0, 'messages,',
            conv?.reference ? 'with references' : 'WITHOUT references');
          
          if (conv?.message) {
            syncConversationWithUI(conversationId, conv.message, conv.reference);
          }
        } catch (err) {
          console.error('BG fetch error:', err);
        }
        */
      } else {
        await loadConversation(conversationId);
      }
    })();
  }, [conversationId]);
  
  /**
   * Load a dialog by ID and conversation ID
   */
  async function loadDialog(newDialogId, newConvId) {
    if (newDialogId === dialogId && newConvId === conversationId) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessages([]); // Clear current messages immediately

    try {
      // Ensure the conversation exists before proceeding
      const conv = await getConversation(newConvId); 
      if (!conv) {
        // This case might happen if the conversation was deleted but dialog still exists somehow
        console.error(`[ChatContext] Conversation ${newConvId} not found during loadDialog.`);
        throw new Error('Conversation data could not be retrieved.');
      }
      
      // Also check dialog for robustness, though less likely to be an issue if conv exists
      try {
        const dialogDetails = await getDialogDetails(newDialogId);
        // Set KB IDs directly when loading a dialog
        setDialogKbIds(dialogDetails.kb_ids || []);
        
        // Cache dialog details for future use
        await saveDialogDetails(newDialogId, {
          id: newDialogId,
          kb_ids: dialogDetails.kb_ids || [],
          project_id: dialogDetails.project_id,
          name: dialogDetails.name || 'Chat'
        });
        
        // Save as active session
        await saveActiveSession(newDialogId, newConvId);
      } catch(dialogErr) {
        console.error(`[ChatContext] Dialog ${newDialogId} not found during loadDialog.`);
        throw new Error('Dialog data could not be retrieved.');
      }

      // Update local state only after validation passes
      setDialogId(newDialogId);
      setConversationId(newConvId);
      
      if (conv.message) {
        // Use the same logic as syncConversationWithUI for proper reference handling
        syncConversationWithUI(newConvId, conv.message, conv.reference);
      }
    } catch (err) {
      console.error('Error loading dialog:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }
  
  /**
   * Create a new dialog
   */
  async function createNewDialog(name = '', projectId = null) {
    setLoading(true);
    setError(null);
    
    try {
      // Create a new dialog with the given name or a default, and the project ID if provided
      const newDialog = await createDialog(name || 'New Chat', projectId);
      const newDialogId = newDialog.id;
      
      // Create a new conversation for this dialog
      const newConv = await createConversation(newDialogId);
      const newConvId = newConv.id;
      
      // Cache dialog details to avoid re-fetching
      await saveDialogDetails(newDialogId, {
        id: newDialogId,
        kb_ids: newDialog.kb_ids || [],
        project_id: projectId,
        name: name || 'New Chat'
      });
      
      // Save as active session
      await saveActiveSession(newDialogId, newConvId);
      
      // Update state
      setDialogId(newDialogId);
      setConversationId(newConvId);
      setMessages([]);
      
      // Add new dialog to dialogs list
      setDialogs(prevDialogs => [
        ...prevDialogs,
        {
          id: newDialogId,
          name: name || 'New Chat',
          create_time: new Date().toISOString(),
          status: "1"
        }
      ]);
      
      return { dialogId: newDialogId, conversationId: newConvId };
    } catch (err) {
        console.error('[ChatContext.createNewDialog] Error creating dialog:', err);
        setError(err);
        throw err; // Re-throw for modal error handling
    } finally {
        setLoading(false);
    }
  }
  
  /**
   * Load a conversation from the server
   */
  async function loadConversation(cid) {
    setLoading(true);
    setError(null);
    try {
      const conv = await getConversation(cid);
      if (conv?.message) {
        syncConversationWithUI(cid, conv.message, conv.reference);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  
  /**
   * Merge new server data with any existing reference objects.
   */
  function syncConversationWithUI(cid, messageArray, referenceArray) {
    // filter out unwanted system prompts, etc.
    const filtered = messageArray.filter(
      (m) => !(m.role === 'assistant' && m.content.includes('您好，我是您的助手小樱'))
    );

    setMessages(prev => {
      // Create a map of existing references by content for better matching
      const prevReferenceMap = new Map();
      prev.forEach(msg => {
        if (msg.reference && msg.content) {
          prevReferenceMap.set(msg.content, msg.reference);
        }
      });

      const loaded = filtered.map((m, idx) => {
        // Determine the reference to use
        let reference = null;
        let referenceSource = 'none';
        
        // Priority 1: If server provides a valid reference, use it
        if (Array.isArray(referenceArray) && referenceArray[idx] !== undefined && referenceArray[idx] !== null) {
          // Only use server reference if it has actual content (chunks array with items)
          if (referenceArray[idx].chunks && referenceArray[idx].chunks.length > 0) {
            reference = referenceArray[idx];
            referenceSource = 'server';
          }
        }
        // Priority 2: Try to match by content to preserve existing reference
        else if (prevReferenceMap.has(m.content)) {
          reference = prevReferenceMap.get(m.content);
          referenceSource = 'content-match';
        }
        // Priority 3: Try to match by index if content doesn't match
        else if (prev[idx] && prev[idx].role === m.role && prev[idx].reference) {
          reference = prev[idx].reference;
          referenceSource = 'index-match';
        }
        
        if (m.role === 'assistant' && referenceSource !== 'none') {
          console.debug(`[ChatContext] Message ${idx} reference source: ${referenceSource}`);
        }
        
        return {
          id: `msg-${idx}`,
          role: m.role,
          content: m.content,
          reference: reference
        };
      });

      // Count references for debugging
      const referencesAfterSync = loaded.filter(m => m.reference && m.role === 'assistant').length;
      console.debug(`[ChatContext] After sync: ${referencesAfterSync} assistant messages have references`);

      // persist
      saveConversationToCache(
        cid,
        loaded.map(m => ({
          role: m.role,
          content: m.content,
          reference: m.reference,
        }))
      );

      return loaded;
    });
  }
  
  /**
   * Send a message to the AI and get a response
   */
  async function sendMessage(message) {
    if (!message.trim()) return;
    if (!dialogId) {
      setError('No active dialog.');
      return;
    }

    const userMsgId = `user-${Date.now()}`;
    const userMessage = {
      id: userMsgId,
      role: 'user',
      content: message,
    };
    const asstMsgId = `assistant-${Date.now()}`;
    const assistantMessage = {
      id: asstMsgId,
      role: 'assistant',
      content: '',
      loading: true,
      reference: null,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    try {
      // Handle different agent modes
      if (agentMode === AGENT_TYPES.BLENDER) {
        // For Blender agent mode, we'll use MCP commands
        await handleBlenderAgentMessage(message, asstMsgId);
        return;
      }
      
      // Default behavior for Ask mode and other modes
      let convId = conversationId;
      if (!convId) {
        const newConv = await createConversation(dialogId);
        convId = newConv.id;
        setConversationId(convId);
      }

      const userMessagesCount = messages.filter((m) => m.role === 'user').length;
      if (userMessagesCount === 0) {
        try {
          const truncated = message.length > 50 ? message.slice(0, 50) + '...' : message;
          await updateDialog(dialogId, { name: truncated });
          
          // Update dialog name in local state
          setDialogs(prevDialogs => prevDialogs.map(dialog => 
            dialog.id === dialogId ? { ...dialog, name: truncated } : dialog
          ));
        } catch (err) {
          console.warn('Failed to update dialog name:', err);
        }
      }

      const existingContext = messages
        .filter((m) => !m.loading && m.id !== asstMsgId)
        .map((m) => ({ role: m.role, content: m.content }));
      const newContext = [...existingContext, { role: 'user', content: message }];

      let accumulatedAnswer = '';

      const finalResponseData = await chatCompletion(convId, newContext, (partial) => {
        if (partial && typeof partial.answer === 'string' && partial.answer !== accumulatedAnswer) {
            accumulatedAnswer = partial.answer;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === asstMsgId) {
                  return {
                    ...m,
                    content: accumulatedAnswer,
                  };
                }
                return m;
              })
            );
        }
      });

      console.log("[ChatContext] Final response data received:", finalResponseData);
      
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === asstMsgId) {
            const finalReference = finalResponseData?.reference && Array.isArray(finalResponseData.reference.chunks)
              ? finalResponseData.reference
              : { chunks: [], doc_aggs: [] };
              
            if (!finalResponseData?.reference || !Array.isArray(finalResponseData.reference.chunks)) {
                console.warn("[ChatContext] Final response missing or has invalid reference structure. Using fallback.", finalResponseData?.reference);
            }

            return {
              ...m,
              content: finalResponseData?.answer || accumulatedAnswer || '**Error: No final answer content**',
              loading: false,
              reference: finalReference,
            };
          }
          return m;
        })
      );
      
      const finalMessagesForCache = messages
         .filter(m => m.id !== asstMsgId)
         .map(m => ({ role: m.role, content: m.content, reference: m.reference }));
      
      finalMessagesForCache.push({
         role: 'assistant',
         content: finalResponseData?.answer || accumulatedAnswer,
         reference: finalResponseData?.reference || { chunks: [], doc_aggs: [] }
      });

      saveConversationToCache(convId, finalMessagesForCache);

    } catch (err) {
      console.error('sendMessage error:', err);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === asstMsgId) {
            return { ...m, content: `**Error**: ${err.message}`, loading: false, reference: null };
          }
          return m;
        })
      );
    }
  }
  
  /**
   * Refresh dialog list and create a new one if needed (e.g. after project deletion)
   * This ensures that if the current dialog was deleted, we switch to a valid one
   * @param {boolean} showLoading - Whether to show loading indicator
   * @param {boolean} forceRefresh - Whether to force dialog refresh even if current dialog exists
   * @returns {Promise<{dialogId: string, conversationId: string}>} - Current dialog and conversation IDs
   */
  async function refreshDialogList(showLoading = true, forceRefresh = false) {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('[ChatContext] Refreshing dialog list from server');
      
      // Get current project_id from URL to filter dialogs
      const urlParams = new URLSearchParams(window.location.search);
      const currentProjectId = urlParams.get('project');
      
      // Get fresh list of dialogs (filtered by project if available)
      const serverDialogs = await listDialogs(currentProjectId);
      const validDialogs = serverDialogs.filter(d => d.status === "1");
      console.log(`[ChatContext] Retrieved ${validDialogs.length} valid dialogs from server for project ${currentProjectId || 'all'}`);
      
      // Update local state with the latest dialogs
      setDialogs(validDialogs);
      
      // If current dialog ID doesn't exist in updated list, or if there are no dialogs at all, or if no dialog is loaded
      const currentDialogExists = validDialogs.some(d => d.id === dialogId);
      
      if (!dialogId || (!currentDialogExists && dialogId) || validDialogs.length === 0 || forceRefresh) {
        if (!dialogId && validDialogs.length > 0) {
          console.log('[ChatContext] No dialog loaded, loading first available dialog');
        } else if (!currentDialogExists && dialogId) {
          console.log(`[ChatContext] Current dialog ${dialogId} no longer exists, finding or creating a new one`);
        } else if (validDialogs.length === 0) {
          console.log('[ChatContext] No dialogs exist, creating first dialog');
        } else if (forceRefresh) {
          console.log('[ChatContext] Forced dialog refresh requested');
        }
        
        if (validDialogs.length > 0) {
          // Use the first valid dialog if any exist
          const nextDialog = validDialogs[0];
          console.log(`[ChatContext] Found existing dialog ${nextDialog.id}, loading it`);
          
          const convs = await listConversations(nextDialog.id);
          const convId = convs.length > 0 ? convs[0].id : (await createConversation(nextDialog.id)).id;
          await loadDialog(nextDialog.id, convId);
        } else {
          // No dialogs exist => create a new one with the current project_id
          console.log(`[ChatContext] No valid dialogs found for project ${currentProjectId || 'none'}, creating a new one`);
          await createNewDialog('New Chat', currentProjectId);
        }
      } else {
        console.log(`[ChatContext] Current dialog ${dialogId} is still valid, no change needed`);
      }
      
      return { dialogId, conversationId };
    } catch (err) {
      console.error('[ChatContext] Error refreshing dialog list:', err);
      setError('Failed to refresh dialogs');
      throw err;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }
  
  /**
   * Remove a dialog from the server & from local state.
   * If the currently-active dialog is deleted, automatically load or create another one.
   */
  async function removeDialogById(dialogIdToRemove) {
    try {
      setLoading(true);
      await removeDialog(dialogIdToRemove);
      
      // Optimistically remove from local state
      setDialogs(prev => prev.filter(d => d.id !== dialogIdToRemove));
      
      // If the user was viewing this dialog, we need to handle fallback
      if (dialogIdToRemove === dialogId) {
        // Clear local state of active IDs
        await clearActiveSession();
        setDialogId(null);
        setConversationId(null);
        
        // Refresh dialog list to find or create a new dialog
        await refreshDialogList(false);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to delete dialog:', err);
      setError('Failed to delete dialog');
      throw err;
    } finally {
      setLoading(false);
    }
  }
  
  /**
   * Find or create a dialog for a specific project
   * If a dialog for this project already exists, use that; otherwise create a new one
   */
  async function findOrCreateDialogForProject(projectId) {
    if (!projectId) {
      setError('No project ID provided');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // First check if there's an existing dialog for this project by checking all dialogs in parallel
      const dialogsWithDetails = await Promise.all(
        dialogs.map(async dialog => {
          const details = await getJsonLocalForage(`dialogDetails-${dialog.id}`);
          return { 
            dialog, 
            details,
            matchesProject: details && details.project_id === projectId
          };
        })
      );
      
      // Find the first dialog that matches this project
      const matchingDialogInfo = dialogsWithDetails.find(info => info.matchesProject);
      
      if (matchingDialogInfo) {
        const existingDialog = matchingDialogInfo.dialog;
        console.log(`Found existing dialog ${existingDialog.id} for project ${projectId}`);
        
        // Use the existing dialog
        const convs = await listConversations(existingDialog.id);
        const convId = convs && convs.length > 0 ? convs[0].id : null;
        
        if (convId) {
          // Load existing dialog and conversation
          await loadDialog(existingDialog.id, convId);
          return { dialogId: existingDialog.id, conversationId: convId };
        } else {
          // Create a new conversation for this dialog
          const newConv = await createConversation(existingDialog.id);
          setDialogId(existingDialog.id);
          setConversationId(newConv.id);
          setMessages([]);
          return { dialogId: existingDialog.id, conversationId: newConv.id };
        }
      } else {
        // Create a new dialog for this project
        console.log(`Creating new dialog for project ${projectId}`);
        return await createNewDialog(`Chat for Project`, projectId); 
      }
    } catch (err) {
      console.error('Error finding/creating dialog for project:', err);
      setError('Failed to initialize chat for project');
      return null;
    } finally {
      setLoading(false);
    }
  }
  
  /**
   * Handle Blender agent mode messages using MCP commands
   */
  async function handleBlenderAgentMessage(message, assistantMsgId) {
    try {
      // For now, we'll implement a simple command parser
      // In the future, this could be enhanced with more sophisticated NLP
      
      let response = '';
      
      // Check for common Blender commands
      if (message.toLowerCase().includes('scene info') || message.toLowerCase().includes('get scene')) {
        const result = await executeBlenderMCP('get_scene_info', {}, currentBlenderServer);
        response = `**Scene Information:**\n\n${result}`;
      } else if (message.toLowerCase().includes('polyhaven') || message.toLowerCase().includes('poly haven')) {
        const result = await executeBlenderMCP('get_polyhaven_status', {}, currentBlenderServer);
        response = `**PolyHaven Status:**\n\n${result}`;
      } else if (message.toLowerCase().includes('hyper3d') || message.toLowerCase().includes('hyper 3d')) {
        const result = await executeBlenderMCP('get_hyper3d_status', {}, currentBlenderServer);
        response = `**Hyper3D Status:**\n\n${result}`;
      } else if (message.toLowerCase().includes('analyze') || message.toLowerCase().includes('suggest')) {
        const result = await executeBlenderMCP('analyze_scene_with_ai', { user_request: message }, currentBlenderServer);
        response = `**AI Scene Analysis:**\n\n${result.analysis || result}`;
      } else if (message.toLowerCase().includes('generate code') || message.toLowerCase().includes('create code')) {
        const result = await executeBlenderMCP('generate_blender_code', { user_request: message }, currentBlenderServer);
        response = `**Generated Blender Code:**\n\n\`\`\`python\n${result.code || result}\n\`\`\``;
      } else {
        // Default response for Blender agent mode
        response = `I'm your Blender Agent! I can help you with 3D modeling tasks. Here are some things I can do:

**Scene Management:**
- Get scene information
- Get object details
- Take viewport screenshots

**AI-Powered Features:**
- Analyze scene and get AI suggestions (try "analyze my scene" or "suggest improvements")
- Generate Blender Python code (try "generate code to create a cube" or "create code for lighting")

**Asset Integration:**
- Check PolyHaven status and download assets
- Check Sketchfab status and download models
- Check Hyper3D status and generate 3D models

**Code Execution:**
- Execute Python code in Blender

Try asking me to analyze your scene, generate code, or check the status of various integrations!`;
      }
      
      // Update the assistant message with the response
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === assistantMsgId) {
            return {
              ...m,
              content: response,
              loading: false,
            };
          }
          return m;
        })
      );
      
    } catch (error) {
      console.error('Error in Blender agent mode:', error);
      
      // Update the assistant message with error
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === assistantMsgId) {
            return {
              ...m,
              content: `**Error in Blender Agent Mode:** ${error.message}`,
              loading: false,
            };
          }
          return m;
        })
      );
    }
  }
  
  // Prepare the context value
  const value = {
    // State
    dialogs,
    dialogId,
    conversationId,
    messages,
    loading,
    error,
    dialogKbIds, // Expose KB IDs through the context
    agentMode, // Add agent mode to context
    currentBlenderServer, // Add current Blender server to context
    
    // State Setters (Added)
    setDialogId,
    setConversationId,
    setMessages,
    setAgentMode, // Add agent mode setter
    setCurrentBlenderServer, // Add Blender server setter
    
    // Actions
    loadDialog,
    createNewDialog,
    sendMessage,
    refreshDialogList,
    removeDialogById,
    findOrCreateDialogForProject,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

/**
 * Custom hook to use the chat context
 */
export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
} 